import React, { useEffect, useState } from 'react';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { IoAdd, IoAddOutline, IoClose, IoRemove, IoSearch } from 'react-icons/io5';
import { query, collection, onSnapshot, orderBy, setDoc, doc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../../../firebase';
import { useAuth } from '../../../../context/AuthContext';
import Datatable from '../../../../components/expenses/categories/datatable';

function Categories({ showCategoryModal, setShowCategoryModal }) {

  const { user } = useAuth();
  const { bid } = user.claims;

  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [categoryData, setCategoryData] = useState({ name: '' });
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [showAddUpdateCategoryModal, setShowAddUpdateCategoryModal] = useState(false);
  const [inputError, setInputError] = useState(null);

  const getCategories = () => {
    const q = query(collection(db, "Businesses", bid, "ExpenseCategories"), orderBy('name'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempData = [];
      querySnapshot.forEach((doc) => {
        tempData.push({ id: doc.id, ...doc.data() });
      });

      setData(tempData);
      setIsLoading(false);
    }, (error) => {
      console.log(error.message);
      setIsLoading(false);
    });

    return () => unsubscribe;
  }

  useEffect(() => {
    getCategories();
  }, []);

  async function handleCategoryData(data) {
    let ecid = uuidv4();
    data = { ...data, addedOn: new Date(), updatedOn: new Date() };
    try {
      await setDoc(doc(db, "Businesses", bid, "ExpenseCategories", ecid), data);
      setCategoryData({
        name: ''
      });
      setShowAddUpdateCategoryModal(false);
      setTimeout(() => {
        setShowCategoryModal(true);
      }, 250);
    } catch (error) {
      console.log('Adding Expense Category & Sub Categories Data [Error] : ', error);
    } finally {
      subCategoryData && subCategoryData.forEach(async (item) => {
        let scid = uuidv4();
        item = { ...item, addedOn: new Date(), updatedOn: new Date() };
        await setDoc(doc(db, "Businesses", bid, "ExpenseCategories", ecid, "SubCategories", scid), item);
      });
    }
  }

  if (showAddUpdateCategoryModal) {
    return (
      <Modal
        show={showAddUpdateCategoryModal}
        onHide={() => {
          setCategoryData({ name: '' });
          setSubCategoryData([]);
          setShowAddUpdateCategoryModal(false);
          setTimeout(() => {
            setShowCategoryModal(true);
          }, 250);
        }}
        backdrop="static"
        keyboard={false}
        contentClassName='border-0 shadow-sm'
        centered
      >
        <div className='bg-light overflow-hidden rounded'>
          <div className='d-flex align-items-center justify-content-between'>
            {
              categoryData && categoryData.id ?
                <h5 className='mb-0 p-3'>Edit Category</h5>
                :
                <h5 className='mb-0 p-3'>Add New Category</h5>
            }
            <Button
              variant='white'
              className='rounded border-0 px-3'
              onClick={() => {
                setCategoryData({ name: '' });
                setSubCategoryData([]);
                setShowAddUpdateCategoryModal(false);
                setTimeout(() => {
                  setShowCategoryModal(true);
                }, 250);
              }}
            >
              <IoClose size={20} />
            </Button>
          </div>
        </div>
        <div className='border-top border-bottom px-3 py-3'>
          <Form.Group>
            <Form.Label>Category *</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                placeholder="Name of expense category"
                onChange={(event) => setCategoryData({ ...categoryData, name: event.target.value })}
                value={categoryData && categoryData.name}
              />
            </InputGroup>
          </Form.Group>
          <Form.Group>
            <Form.Label>Description <small>(Optional)</small></Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                as={'textarea'}
                rows={3}
                placeholder="Category description"
                onChange={(event) => setCategoryData({ ...categoryData, description: event.target.value })}
                value={categoryData && categoryData.description}
              />
            </InputGroup>
          </Form.Group>
          <Form.Group>
            <Form.Label>Sub Category *</Form.Label>
          </Form.Group>
          {
            subCategoryData && subCategoryData.map((_, index) => {
              return (
                <Form.Group key={index}>
                  <InputGroup className='mb-3'>
                    <Form.Control
                      disabled
                      readOnly
                      placeholder="Enter name"
                      className='border-right-0'
                      defaultValue={_.name}
                    />
                    <Button
                      variant='danger'
                      className='border-left-0 px-3 ms-1'
                      onClick={() => {
                        let tempData = [...subCategoryData];
                        tempData.splice(index, 1);
                        setSubCategoryData(tempData);
                      }}
                    >
                      <IoRemove size={20} />
                    </Button>
                  </InputGroup>
                </Form.Group>
              )
            })
          }
          <Form.Group>
            <InputGroup className='mb-3'>
              <Form.Control
                placeholder="Enter sub category"
                className={inputError && inputError.text ? 'border-right-0 border-danger' : 'border-right-0'}
                onChange={(event) => setText(event.target.value)}
                value={text}
                isInvalid={inputError && inputError.text}
              />
              <Button
                variant='dark'
                className='border-left-0 px-3 ms-1'
                onClick={() => {
                  if (text) {
                    let tempData = [...subCategoryData];
                    tempData.push({ name: text });
                    setSubCategoryData(tempData);
                    setText("");
                    setInputError({ text: "" });
                  } else {
                    setInputError({ text: "This field is required." });
                  }
                }}
              >
                <IoAdd size={20} />
              </Button>
            </InputGroup>
            <Form.Control.Feedback type="invalid">{inputError && inputError.text}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className='d-flex align-items-center justify-content-end px-3 pb-3 mt-3'>
          <Button
            disabled={isAdding && isAdding}
            variant="light"
            onClick={() => {
              setCategoryData({ name: '' });
              setSubCategoryData([]);
              setShowAddUpdateCategoryModal(false);
              setTimeout(() => {
                setShowCategoryModal(true);
              }, 250);
            }}
            className='border px-5 py-2 me-3'
          >
            Cancel
          </Button>
          <Button
            disabled={!categoryData || !subCategoryData || subCategoryData.length === 0 || categoryData && !categoryData.name || isAdding}
            variant="primary"
            className='px-5 py-2 btn-sm-width'
            onClick={() => handleCategoryData(categoryData)}
          >
            {
              isAdding && isAdding ?
                <div>
                  <Spinner size='sm' />
                </div>
                :
                <span>Save</span>
            }
          </Button>
        </div>
      </Modal>
    )
  } else {
    return (
      <>
        <Modal
          show={showCategoryModal}
          onHide={() => setShowCategoryModal(false)}
          size='lg'
          backdrop="static"
          keyboard={false}
          contentClassName='border-0 shadow-sm'
          centered
        >
          <div className='bg-light overflow-hidden rounded'>
            <div className='modal-header'>
              <h5 className='mb-0'>Manage Categories</h5>
              <Button
                variant='none'
                className='btn-close px-3'
                onClick={() => {
                  setCategoryData({ name: '' });
                  setSubCategoryData([]);
                  setShowCategoryModal(false);
                }}
              />
            </div>
            <div className='row'>
              <div className='col-lg-12'>
                {
                  isLoading ?
                    <div className='data-loading-container'>
                      <div className='loader'>
                        <div className='spinner' />
                      </div>
                    </div>
                    :
                    <>
                      <div className='bg-white p-3'>
                        <Form>
                          <div className='row'>
                            <div className='col-9'>
                              <div className='position-relative'>
                                <div className='position-absolute left-0 top-0 bottom-0 ps-3 d-flex align-items-center justify-content-center'>
                                  <IoSearch size={20} color={`var(--bs-placeholder)`} />
                                </div>
                                <Form.Control
                                  type="text"
                                  placeholder="Search by category or sub category name"
                                  onChange={(e) => null}
                                  className='ps-5'
                                />
                              </div>
                            </div>
                            <div className='col-3 d-flex justify-content-end'>
                              <Button
                                variant="primary"
                                type="button"
                                className='py-2 px-3 w-100'
                                onClick={() => {
                                  setShowCategoryModal(false);
                                  setTimeout(() => {
                                    setShowAddUpdateCategoryModal(true);
                                  }, 250);
                                }}
                              >
                                <IoAddOutline size={20} />
                                <span className='ps-2'>New Category</span>
                              </Button>
                            </div>
                          </div>
                        </Form>
                      </div>
                      <Datatable
                        columns={["#", "Category Name", "Sub Categories", ""]}
                        data={data}
                      />
                    </>
                }
              </div>
            </div>
          </div>
        </Modal>
      </>
    )
  }
}

export default Categories;
